import { useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { Plus, RefreshCw, Copy, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FileIcon, getFileIconType } from './FileIcon';
import { TreeChevron } from './TreeChevron';
import { CountBadge } from './CountBadge';
import type { FileNode } from './types';

interface TreeItemProps {
  file: FileNode;
  depth?: number;
  isSelected?: boolean;
  isExpanded?: boolean;
  isLoading?: boolean;
  isRefreshing?: boolean;
  isSyncInProgress?: boolean;
  itemCount?: number;
  onClick?: () => void;
  /**
   * Double-click on a file row. Only wired for files — the caller decides what
   * "open for good" means; the row just reports the gesture.
   */
  onDoubleClick?: () => void;
  onAddFile?: () => void;
  onRefresh?: () => void;
  /** Called when the user clicks the copy-name button on a file node. */
  onCopyName?: () => void;
}

const INDENT_SIZE = 16;
const BASE_PADDING = 12;
// Row height: py-1.5 (6+6px padding) + text-sm line-height (20px) = 32px
const ROW_HEIGHT = 32;
// Icon button size: h-3.5 w-3.5 icon (14px) + no padding (centered by flex)
const ICON_BTN_WIDTH = 22;

const SPRING_CONFIG = { tension: 220, friction: 22 };

export function TreeItem({
  file,
  depth = 0,
  isSelected,
  isExpanded,
  isLoading,
  isRefreshing,
  isSyncInProgress,
  itemCount,
  onClick,
  onDoubleClick,
  onAddFile,
  onRefresh,
  onCopyName,
}: TreeItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const isFolder = file.type === 'folder';
  const iconType = isFolder ? 'folder' : getFileIconType(file.metadataType, file.name);
  const showFolderActions = isFolder && depth === 0 && !isSyncInProgress;
  const showCopyAction = !isFolder && !!onCopyName;

  const addVisible = isHovered && showFolderActions && !!onAddFile;
  const refreshVisible = showFolderActions && !!onRefresh && (isHovered || !!isRefreshing);
  const copyVisible = isHovered && showCopyAction;

  const addSpring = useSpring({
    width: addVisible ? ICON_BTN_WIDTH : 0,
    opacity: addVisible ? 1 : 0,
    config: SPRING_CONFIG,
  });

  const refreshSpring = useSpring({
    width: refreshVisible ? ICON_BTN_WIDTH : 0,
    opacity: refreshVisible ? 1 : 0,
    config: SPRING_CONFIG,
  });

  const copySpring = useSpring({
    width: copyVisible ? ICON_BTN_WIDTH : 0,
    opacity: copyVisible ? 1 : 0,
    config: SPRING_CONFIG,
  });

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddFile?.();
  };

  const handleRefreshClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRefresh?.();
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCopyName?.();
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1200);
  };

  return (
    <button
      type="button"
      data-tree-item-id={file.id}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group flex w-full items-center gap-1.5 rounded py-1.5 text-sm transition-colors',
        'border-l-2 border-l-transparent',
        isFolder && 'sticky bg-background',
        'hover:bg-file-explorer-hover hover:text-file-explorer-hover-foreground',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1',
        isSelected &&
          'bg-file-explorer-selected text-file-explorer-selected-foreground border-l-file-explorer-selected-border'
      )}
      style={{
        paddingLeft: `${depth * (INDENT_SIZE + BASE_PADDING)}px`,
        paddingRight: `${BASE_PADDING}px`,
        // Sticky folders stack: each depth level sticks just below the parent's sticky header.
        // z-index uses a low range (2–20) so the tree stays below modals/popovers (typically 50+).
        // Formula preserves parent > guide > child; must stay in sync with guideZ in TreeItemWrapper.tsx.
        ...(isFolder && {
          top: `${depth * ROW_HEIGHT}px`,
          zIndex: Math.max(20 - 2 * depth, 2),
        }),
      }}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      {isFolder && (
        <TreeChevron isExpanded={!!isExpanded} isLoading={isLoading} />
      )}

      <FileIcon type={iconType} isExpanded={isExpanded} isLoading={isLoading} />

      <span className="flex-1 truncate text-left">{file.name}</span>

      {isFolder && depth === 0 && onAddFile && !isSyncInProgress && (
        <animated.span
          role="button"
          tabIndex={addVisible ? 0 : -1}
          onClick={handleAddClick}
          onKeyDown={(e) => e.key === 'Enter' && handleAddClick(e as unknown as React.MouseEvent)}
          style={{
            width: addSpring.width,
            opacity: addSpring.opacity,
            overflow: 'hidden',
            flexShrink: 0,
          }}
          className="h-[22px] flex items-center justify-center rounded hover:bg-primary/20"
          title="Add new item"
        >
          <Plus className="h-3.5 w-3.5 text-foreground shrink-0" />
        </animated.span>
      )}

      {isFolder && depth === 0 && onRefresh && !isSyncInProgress && (
        <animated.span
          role="button"
          tabIndex={refreshVisible ? 0 : -1}
          onClick={handleRefreshClick}
          onKeyDown={(e) => e.key === 'Enter' && handleRefreshClick(e as unknown as React.MouseEvent)}
          style={{
            width: refreshSpring.width,
            opacity: refreshSpring.opacity,
            overflow: 'hidden',
            flexShrink: 0,
          }}
          className="h-[22px] flex items-center justify-center rounded hover:bg-primary/20"
          title="Refresh this metadata type"
        >
          <RefreshCw
            className={cn(
              'h-3.5 w-3.5 text-foreground shrink-0',
              isRefreshing && 'animate-spin text-primary'
            )}
          />
        </animated.span>
      )}

      {isFolder && depth === 0 && isRefreshing && (!onRefresh || isSyncInProgress) && (
        <span className="h-[22px] w-[22px] flex items-center justify-center shrink-0">
          <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary" />
        </span>
      )}

      {isFolder && itemCount !== undefined && itemCount > 0 && (
        <CountBadge value={itemCount} className="ml-auto" />
      )}

      {showCopyAction && (
        <animated.span
          role="button"
          tabIndex={copyVisible ? 0 : -1}
          onClick={handleCopyClick}
          onKeyDown={(e) => e.key === 'Enter' && handleCopyClick(e as unknown as React.MouseEvent)}
          style={{
            width: copySpring.width,
            opacity: copySpring.opacity,
            overflow: 'hidden',
            flexShrink: 0,
          }}
          className="h-[22px] flex items-center justify-center rounded hover:bg-primary/20"
          title="Copy name"
        >
          {isCopied ? (
            <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
          ) : (
            <Copy className="h-3.5 w-3.5 text-foreground shrink-0" />
          )}
        </animated.span>
      )}
    </button>
  );
}
